// Pièces d'un dossier.
//
// Le fichier est chiffré dans ce navigateur, puis envoyé DIRECTEMENT au stockage
// objet par une URL présignée : le ciphertext ne traverse jamais l'API. Le
// serveur ne connaît ni le contenu, ni le nom, ni le type — seulement une taille
// et deux blobs opaques.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  downloadDocument,
  listDocuments,
  uploadDocument,
  type Ctx,
  type DocumentEntry,
} from './engine';
import type { DossierState } from '../crypto/mls-state';

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

function taille(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${(octets / 1024 / 1024).toFixed(1)} Mo`;
}

export function Documents({
  ctx,
  dossierId,
  state,
}: {
  ctx: Ctx;
  dossierId: string;
  state: DossierState;
}) {
  const [docs, setDocs] = useState<DocumentEntry[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setDocs(await listDocuments(ctx, dossierId, state));
    } catch (e) {
      setError(message(e));
    }
  }, [ctx, dossierId, state]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onUpload(file: File) {
    setBusy(true);
    setError(null);
    try {
      await uploadDocument(ctx, dossierId, state, file);
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  async function onDownload(doc: DocumentEntry) {
    setError(null);
    try {
      await downloadDocument(ctx, dossierId, doc);
    } catch (e) {
      setError(message(e));
    }
  }

  return (
    <section className="mt-8 border-t border-slate-200 pt-4" data-testid="documents">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Pièces du dossier
      </h3>

      {!state.dossierKey && (
        <p className="mt-2 text-xs text-amber-700" data-testid="no-dossier-key">
          Clé du dossier pas encore reçue : les pièces resteront illisibles tant
          qu&apos;un membre ne te l&apos;aura pas transmise par le groupe chiffré.
        </p>
      )}

      {error && (
        <p data-testid="documents-error" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}

      <ul className="mt-3 space-y-2" data-testid="document-list">
        {docs?.length === 0 && (
          <li className="text-sm text-slate-400">Aucune pièce.</li>
        )}
        {docs?.map((d) => (
          <li key={d.id} className="flex items-center gap-3 text-sm">
            {d.meta ? (
              <>
                <button
                  type="button"
                  onClick={() => onDownload(d)}
                  data-testid="download-document"
                  className="truncate text-left text-blue-900 underline hover:text-blue-700"
                >
                  {d.meta.name}
                </button>
                <span className="ml-auto shrink-0 text-xs text-slate-400">
                  {taille(d.sizeBytes)}
                </span>
              </>
            ) : (
              <span className="text-slate-400">
                <em>pièce illisible sur cet appareil</em>
              </span>
            )}
          </li>
        ))}
      </ul>

      <label className="mt-4 block">
        <span className="sr-only">Déposer une pièce</span>
        <input
          ref={input}
          type="file"
          disabled={busy || !state.dossierKey}
          data-testid="upload-document"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onUpload(file);
          }}
          className="w-full text-xs text-slate-600 file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-200 disabled:opacity-50"
        />
      </label>

      <p className="mt-2 text-[10px] text-slate-400">
        {busy
          ? 'Chiffrement et dépôt…'
          : 'La pièce est chiffrée ici, puis déposée directement sur le stockage. Le serveur ne la voit jamais.'}
      </p>
    </section>
  );
}
